/**
 * Application Service: Analytic Plan Editor Component
 *
 * This application service orchestrates the analytic plan editing workflow.
 * It coordinates between the domain entities and the UI screen data.
 *
 * Responsibilities:
 * - Loading company configuration
 * - Converting data between formats (API dicts to domain entities)
 * - Assigning plans to lines
 * - Applying automatic distribution rules
 * - Managing UI state (which plans are visible)
 *
 * @module analitic_accounting/application/AnalyticsPlanEditorComponent
 */

import { AnalyticPlanEditorScreenData } from '../domain/AnalyticsPlanEditorScreenData'
import { AnalyticPlan, Line, LineAnalyticPlan, LineLevel } from '../domain/Company'
import { CompanyRepository } from '../domain/CompanyRepository'

/**
 * Recursively converts template Level objects to LineLevel objects with amounts.
 * Preserves ui_visible property if it exists in the source data.
 *
 * @param levels - Array of level objects (can be from template or from API)
 * @returns Array of LineLevel domain entities
 */
const create_line_levels_from_levels = (levels) => {
    if(!levels){
        return []
    }
    let line_levels = []
    levels.forEach((l: any) => {
        const lineLevel = new LineLevel(l.code, l.name, create_line_levels_from_levels(l.levels), l.amount);
        // Preserve ui_visible if it exists in the source data
        if (l.ui_visible !== undefined && l.ui_visible !== null) {
            lineLevel.ui_visible = l.ui_visible;
        }
        line_levels.push(lineLevel);
    })
    return line_levels
}

/**
 * Creates a LineAnalyticPlan from a template AnalyticPlan.
 * Used when assigning a plan to a line for the first time.
 *
 * @param plan - The template analytic plan
 * @param debit - Debit amount of the line
 * @param credit - Credit amount of the line
 * @returns LineAnalyticPlan with empty amounts (to be filled by user or auto-distribution)
 */
const create_line_analytic_plan_from_analityc_plan = (plan: AnalyticPlan, debit: number, credit: number) => {
    let line_levels = create_line_levels_from_levels(plan.levels)
    let totalAmount: number = debit + credit;
    return new LineAnalyticPlan(plan.code, plan.name, line_levels, totalAmount)

}

/**
 * Creates a LineAnalyticPlan from a plain dictionary object.
 * Used when loading existing data from the API/backend.
 * Always uses backend data directly without any filtering or merging.
 *
 * @param plan_dict - Plain object containing plan data
 * @param amount - Total amount for this plan
 * @returns LineAnalyticPlan domain entity
 */
const create_line_analytic_plan_from_dict = (plan_dict, amount) => {
    // Always use backend data directly - display exactly what backend sends
    const line_levels = create_line_levels_from_levels(plan_dict.levels);
    return new LineAnalyticPlan(plan_dict.code, plan_dict.name, line_levels, amount);
}

/**
 * Application service for managing analytic plan editing.
 * Coordinates the flow between repositories, domain entities, and UI state.
 */
export class AnalyticPlanEditorComponent{
    /** Repository for fetching company configuration */
    company_repo:CompanyRepository

    /** Reactive screen data that the UI binds to */
    screen_data: AnalyticPlanEditorScreenData

    /**
     * Creates the editor component
     * @param company_repo - Repository to load company data
     * @param screen_data - Reactive screen data object
     */
    constructor(company_repo: CompanyRepository, screen_data: AnalyticPlanEditorScreenData){
        this.company_repo = company_repo
        this.screen_data = screen_data
    }

    /**
     * Shows the plan_1 popup/details for a specific line.
     * Hides all other plans to ensure only one is shown at a time.
     *
     * @param index - The line index to show plan_1 for
     */
    show_plan_1(index: number) {
        console.log("blackraven - AnalyticPlanEditorComponent - show_plan_1: ", index);
        this.screen_data.lines.forEach(line => {
          line.ui_show_plan_1 = false;
          line.ui_show_plan_2 = false;
        });
        this.screen_data.lines[index].ui_show_plan_1 = true;
      }

    /**
     * Shows the plan_2 popup/details for a specific line.
     * Hides all other plans to ensure only one is shown at a time.
     *
     * @param index - The line index to show plan_2 for
     */
    show_plan_2(index: number) {
        console.log("blackraven - AnalyticPlanEditorComponent - show_plan_2: ", index, this.screen_data.lines);
        this.screen_data.lines.forEach(line => {
          line.ui_show_plan_1 = false;
          line.ui_show_plan_2 = false;
        });
        this.screen_data.lines[index].ui_show_plan_2 = true;
      }

    /**
     * Converts a dictionary of line data (from API) to an array of Line domain entities.
     * Handles reconstruction of analytic plans with their levels and amounts.
     * Uses backend data directly without any filtering or template merging.
     *
     * @param lines - Dictionary where keys are line numbers and values are line data
     * @returns Array of Line domain entities ready for editing
     */
    dict_to_line(lines: any): Line[] {;
        console.log("blackraven - AnalyticPlanEditorComponent - dict_to_line: ", lines)
        let line_count = 0;
        let result: Line[] = []
        for (const [key, value] of Object.entries(lines)) {
            const v = value as {id:any; account: string; plan_1?: any; plan_2?: any ,debit?: any,credit?: any}
            let line = new Line(parseInt(v.id),v.account)
            line.line_number = line_count++;
            line.plan_1 = null
            line.plan_2 = null

            if(v.plan_1 && v.plan_1.levels) {
                // Use backend data directly
                line.plan_1 = create_line_analytic_plan_from_dict(v.plan_1, v.debit+v.credit);
            }

            if(v.plan_2 && v.plan_2.levels) {
                // Use backend data directly
                line.plan_2 = create_line_analytic_plan_from_dict(v.plan_2, v.debit+v.credit);
            }

            result.push(line)
        }
        return result
    }

    /**
     * Initializes the editor component with company data and lines.
     * This is the main entry point called when the editor screen loads.
     *
     * Steps:
     * 1. Fetches company configuration from repository
     * 2. Converts line dictionaries to domain entities (using backend data directly)
     * 3. Validates existing plan amounts
     * 4. Marks levels with amounts as visible for display
     *
     * @param company_id - The company identifier
     * @param lines - Dictionary of line data (from API)
     */
    async mountEditorComponent(company_id: number, lines:any={}): Promise<void> {
        let company = await this.company_repo.find_by_id(company_id)
        this.screen_data.setCompany(company)
        console.log("blackraven - AnalyticPlanEditorComponent.ts - mountEditorComponent: ", lines);
        this.screen_data.setLines(this.dict_to_line(lines))
        for(let i=0; i<this.screen_data.lines.length;i++){
            if(this.screen_data.lines[i].plan_1) {
                this.screen_data.lines[i].plan_1.validate_level_amounts()
                // Mark levels with amounts as visible so they show on first load
                this.markLevelsWithAmountsAsVisible(this.screen_data.lines[i].plan_1.levels)
            }
            if(this.screen_data.lines[i].plan_2) {
                this.screen_data.lines[i].plan_2.validate_level_amounts()
                // Mark levels with amounts as visible so they show on first load
                this.markLevelsWithAmountsAsVisible(this.screen_data.lines[i].plan_2.levels)
            }
        }
    }

    /**
     * Checks if an account code exists in the accounts_plans array.
     *
     * @param account - Account code to search for
     * @param accounts_plans - Array of Account objects
     * @returns true if account is found
     */
    account_plans_contains(account,accounts_plans): boolean{
        let b=false
        for(let i=0;i<accounts_plans.length;i++){
            if(accounts_plans[i].code==account){
                b=true
                break;
            }
        }
        return b
    }

    /**
     * Checks if a SPECIFIC plan is predefined for a given account.
     * This determines whether automatic distribution should be applied.
     *
     * Example: Account 60000001 has LOCALIZACIÓN predefined
     * - isPlanPredefinedForAccount("60000001", "02") -> true (LOCALIZACIÓN)
     * - isPlanPredefinedForAccount("60000001", "01") -> false (DEPARTAMENTOS not predefined)
     *
     * @param account - Account code
     * @param planCode - The code of the plan to check (e.g., "01", "02")
     * @param accounts_plans - Array of Account objects with their predefined plans
     * @returns true if the specific plan is predefined for this account
     */
    private isPlanPredefinedForAccount(account: string, planCode: string, accounts_plans: any[]): boolean {
        const accountPlan = accounts_plans.find(ap => ap.code === account);
        if (!accountPlan || !accountPlan.plans) {
            return false;
        }
        return accountPlan.plans.some(p => p.code === planCode);
    }

    /**
     * Recursively marks levels with defined amounts as visible (ui_visible = true).
     * This is used after automatic distribution to ensure the form displays the populated levels.
     */
    private markLevelsWithAmountsAsVisible(levels: LineLevel[]): void {
        if (!levels || levels.length === 0) return;

        levels.forEach(level => {
            // Mark as visible if amount is defined (not null/undefined)
            if (level.amount !== null && level.amount !== undefined) {
                level.ui_visible = true;
            }
            // Recursively process children
            if (level.levels && level.levels.length > 0) {
                this.markLevelsWithAmountsAsVisible(level.levels);
            }
        });
    }

    /**
     * Assigns an analytic plan to a line, applying automatic distribution if configured.
     * This is the core method for attaching/removing plans from lines.
     *
     * Behavior:
     * - If plan is provided: Creates LineAnalyticPlan, applies auto-distribution, marks levels visible
     * - If plan is null: Removes the plan and hides the UI
     *
     * Automatic Distribution:
     * - Linear: Divides amount equally across all levels
     * - Percentage: Distributes according to template percentages
     * - ONLY applies if the SPECIFIC plan is predefined for this account
     * - If user selects a non-predefined plan, NO levels are selected by default
     *
     * Example: Account 60000001 has only LOCALIZACIÓN predefined
     * - Select LOCALIZACIÓN → automatic distribution applied
     * - Select DEPARTAMENTOS → NO automatic distribution, clean slate
     *
     * @param line - The line to attach the plan to
     * @param plan - The template plan to assign (or null to remove)
     * @param debit - Debit amount
     * @param credit - Credit amount
     * @param planProperty - Which plan slot ('plan_1' or 'plan_2')
     */
    assignAnalyticPlanToLinePlan(line: Line, plan: AnalyticPlan, debit: number, credit: number, planProperty: 'plan_1' | 'plan_2') {
        const planNumber = planProperty === 'plan_1' ? '1' : '2'
        console.log(`blackraven - AnalyticPlanEditorComponent.ts - assignAnalyticPlanToLinePlan${planNumber}: `, line, plan);

        let accounts_plans = this.screen_data.company.accounts_plans
        let analytics_plan_configuration = this.screen_data.company.analytics_plan_configuration

        const uiShowProperty = `ui_show_${planProperty}` as 'ui_show_plan_1' | 'ui_show_plan_2'

        if(plan) {
            line[planProperty] = create_line_analytic_plan_from_analityc_plan(plan, debit, credit)

            // CRITICAL: Only apply automatic distribution if THIS SPECIFIC plan is predefined
            // This prevents auto-selection when user chooses a non-predefined plan
            const isPlanPredefined = this.isPlanPredefinedForAccount(line.account, plan.code, accounts_plans);

            if(analytics_plan_configuration.is_linear_automatic_distribution && isPlanPredefined){
                line[planProperty].set_linear_automatic_distribution()
                // Mark levels with amounts as visible so they appear in the form
                this.markLevelsWithAmountsAsVisible(line[planProperty].levels)
            }

            if(analytics_plan_configuration.is_automatic_percentage_distribution && isPlanPredefined){
                line[planProperty].set_percentage_automatic_distribution(plan)
                // Mark levels with amounts as visible so they appear in the form
                this.markLevelsWithAmountsAsVisible(line[planProperty].levels)
            }

            // Note: If plan is NOT predefined, line[planProperty] is created with empty amounts
            // User must manually select levels and enter amounts
        } else {
            line[planProperty] = null
            // Hide the delete pencil clicked.
            line[uiShowProperty] = false
        }
    }

    /**
     * Convenience method to assign a plan to plan_1 slot.
     * @param line - The line
     * @param plan - The plan to assign
     * @param debit - Debit amount
     * @param credit - Credit amount
     */
    assignAnalyticPlanToLinePlan1(line: Line, plan: AnalyticPlan, debit: number, credit: number) {
        this.assignAnalyticPlanToLinePlan(line, plan, debit, credit, 'plan_1')
    }

    /**
     * Convenience method to assign a plan to plan_2 slot.
     * @param line - The line
     * @param plan - The plan to assign
     * @param debit - Debit amount
     * @param credit - Credit amount
     */
    assignAnalyticPlanToLinePlan2(line: Line, plan: AnalyticPlan, debit: number, credit: number) {
        this.assignAnalyticPlanToLinePlan(line, plan, debit, credit, 'plan_2')
    }

    /**
     * Gets the available analytic plans for a given accounting item/line.
     *
     * Logic:
     * 1. If account has specific plans assigned -> return predefined plans FIRST, then all other company plans
     * 2. If allow_analytics_plans_all_accounts is enabled -> return all company plans
     * 3. Otherwise -> return empty array (no plans available)
     *
     * This allows maximum flexibility: users can select predefined plans OR any other company plan.
     *
     * @param item - Object containing the account code
     * @returns Array of available AnalyticPlan templates (predefined plans first, then others)
     */
    getPlansForItem(item: { account: string }): AnalyticPlan[] {
        const company = this.screen_data.company;
        if (!company) {
            return [];
        }

        const accountsPlans = company.accounts_plans || [];
        const match = accountsPlans.find(ap => ap.code === item.account);

        if (match && match.plans && match.plans.length > 0) {
            // Account has predefined plans
            // Return predefined plans FIRST, then all other company plans (deduplicated)
            const predefinedCodes = new Set(match.plans.map(p => p.code));
            const additionalPlans = company.plans.filter(p => !predefinedCodes.has(p.code));

            // Return predefined plans first for easy access, then additional plans
            return [...match.plans, ...additionalPlans];
        }

        // No predefined plans - return all company plans if allowed
        return company.analytics_plan_configuration.is_allow_analytics_plans_all_accounts
            ? company.plans
            : [];
    }

    /**
     * Gets the predefined analytic plans for a given account code.
     *
     * Logic:
     * 1. If account has specific plans assigned -> return those plans
     * 2. Otherwise -> return empty array (no plans available)
     *
     * Note: Unlike getPlansForItem, this does NOT return company.plans
     * when is_allow_analytics_plans_all_accounts is enabled.
     * This is used when you need to check if an account has explicit predefined plans.
     *
     * @param item - Object containing the account code
     * @returns Array of predefined AnalyticPlan templates for this account, or [] if none
     */
    getPlansForAccount(item: { account: string }): AnalyticPlan[] {
        const company = this.screen_data.company;
        if (!company) {
            return [];
        }

        const accountsPlans = company.accounts_plans || [];
        const match = accountsPlans.find(ap => ap.code === item.account);

        return match ? match.plans : [];
    }

    /**
     * Updates the amounts in both plans when the line's debit/credit changes.
     * Recalculates the total amount and updates all plans, triggering validation.
     *
     * Use case: User changes the line amount in the invoice -> plans need to reflect new total
     *
     * @param lineIndex - Index of the line in the lines array
     * @param debit - New debit amount
     * @param credit - New credit amount
     */
    updateLineAmount(lineIndex: number, debit: number, credit: number) {
        const line = this.screen_data.lines[lineIndex];
        if (!line) {
            return;
        }

        const totalAmount = debit + credit;

        if (line.plan_1) {
            line.plan_1.update_total_amount(totalAmount);
        }

        if (line.plan_2) {
            line.plan_2.update_total_amount(totalAmount);
        }
    }
  }